import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Invoice, InvoiceArticle } from '@thro-types/billing';

import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useInvoiceArticleMutation } from 'api/hooks/billing/invoiceArticles';
import { ConfirmationDialog } from 'app/components';
import { translations } from 'locales/i18n';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';

import { ExistingInvoiceArticleRow, NewInvoiceArticleRow } from './ArticleRow';

interface Props {
  invoice: Invoice;
  editMode: boolean;
}

export interface Ref {
  resetEditingArticle: () => void;
}

export const ArticleConfiguration = forwardRef<Ref, Props>(({ invoice, editMode }: Props, ref) => {
  const { t } = useTranslation();
  const [editingArticle, setEditingArticle] = useState<string | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<InvoiceArticle | undefined>(undefined);

  const { deletion } = useInvoiceArticleMutation();

  const filteredTotals = invoice.taxTotals.filter(taxTotal => taxTotal.tax?.taxRate !== 0);

  const sanitizedTaxTotals = !filteredTotals.length ? invoice.taxTotals : filteredTotals;

  const handleDeleteClick = (article: InvoiceArticle) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const resetEditingArticle = () => {
    setEditingArticle(undefined);
  };

  useImperativeHandle(ref, () => ({ resetEditingArticle }));

  const handleDeleteArticle = async () => {
    try {
      if (!articleToDelete) return;
      await deletion.mutateAsync({ invoiceId: invoice.id, id: articleToDelete.id });
      setDeleteDialogOpen(false);
      setArticleToDelete(undefined);
    } catch (error) {}
  };

  const renderTotalsFooter = () => {
    if (!invoice.invoiceArticles.length && !editMode) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">
            {t(translations.pages.billing.detail.articles.noArticles())}
          </TableCell>
        </TableRow>
      );
    } else {
      return (
        <>
          <TableRow sx={{ borderTop: '2px solid #999' }}>
            <TableCell rowSpan={2 + sanitizedTaxTotals.length} colSpan={4} />
            <TableCell colSpan={2}>{t(translations.common.netto())}</TableCell>
            <TableCell align="right">{formatMonetaryValue(invoice.totalNet || 0)}</TableCell>
          </TableRow>
          {sanitizedTaxTotals.map(taxTotal => (
            <TableRow key={taxTotal.id}>
              <TableCell>{t(translations.common.ust())}</TableCell>
              <TableCell align="right">{`${taxTotal.tax?.taxRate || 0} %`}</TableCell>
              <TableCell align="right">{formatMonetaryValue(taxTotal.totalTax || 0)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={2} style={{ fontWeight: 'bold' }}>
              {t(translations.common.brutto())}
            </TableCell>
            <TableCell align="right" style={{ fontWeight: 'bold' }}>
              {formatMonetaryValue(invoice.totalGross || 0)}
            </TableCell>
          </TableRow>
        </>
      );
    }
  };

  return (
    <div>
      <ConfirmationDialog
        title={t(translations.common.reallyDelete())}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteArticle}
        isPending={deletion.isPending}
      >
        {t(translations.pages.billing.detail.articles.confirmDelete())}
      </ConfirmationDialog>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '15%', fontSize: '1em' }}>
              {t(translations.pages.billing.articleName())}
            </TableCell>
            <TableCell style={{ width: '25%', fontSize: '1em' }}>
              {t(translations.common.description())}
            </TableCell>
            <TableCell style={{ width: '2%', fontSize: '1em' }} align="right">
              {t(translations.common.ust())}
            </TableCell>
            <TableCell style={{ width: '2%', fontSize: '1em' }} align="right">
              {t(translations.common.amount())}
            </TableCell>
            <TableCell style={{ width: '5%', fontSize: '1em' }} align="left">
              {t(translations.common.unit())}
            </TableCell>
            <TableCell style={{ width: '5%', fontSize: '1em' }} align="right">
              {t(translations.common.singlePrice())}
            </TableCell>
            <TableCell style={{ width: '5%', fontSize: '1em' }} align="right">
              {t(translations.common.totalPrice())}
            </TableCell>
            {editMode && (
              <TableCell style={{ width: '8%', fontSize: '1em' }} align="center" size="small">
                {t(translations.common.actions())}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...invoice.invoiceArticles]
            .sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();

              return dateA - dateB;
            })
            .map(article => (
              <ExistingInvoiceArticleRow
                key={article.id}
                article={article}
                invoice={invoice}
                editingArticle={editingArticle}
                setEditingArticle={setEditingArticle}
                handleDeleteClick={handleDeleteClick}
                editMode={editMode}
              />
            ))}
          {editMode && <NewInvoiceArticleRow invoice={invoice} />}
          {renderTotalsFooter()}
        </TableBody>
      </Table>
    </div>
  );
});
